/**
 * template-data — the data seam of Studio's custom HTML templates.
 *
 * Data: bound. Every read and write goes through the typed `@v0y4ge/api-client`:
 *   listPortalExperiencePages · getPortalExperiencePage · putPortalExperiencePage ·
 *   publishPortalExperiencePage · previewPortalExperiencePage · getPortalSettings ·
 *   putPortalSettings · setPortalEventListTemplate · getStudioEvent · creation.updateEvent.
 * Nothing here persists to the browser, and no success is reported before the
 * server answered: every assignment is read back through its canonical GET and the
 * caller compares what came back with what it asked for.
 *
 * WHAT A "TEMPLATE" IS HERE. Not a new record type: a custom HTML template is an
 * ordinary One-Stop experience page whose settings say `layout: 'custom-html'`.
 * `pageKind: 'page'` templates can be served in the event-listing slot;
 * `pageKind: 'event-detail'` templates serve an event's detail page. Only a
 * PUBLISHED page of the right kind may be chosen (`isPortalTemplateCandidate`,
 * the same predicate the server checks).
 */
import {
  creation,
  getPortalExperiencePage,
  getPortalSettings,
  getStudioEvent,
  listPortalExperiencePages,
  previewPortalExperiencePage,
  publishPortalExperiencePage,
  putPortalExperiencePage,
  putPortalSettings,
  setPortalEventListTemplate,
} from "@v0y4ge/api-client";
import {
  PORTAL_RESERVED_PAGE_SLUGS,
  PORTAL_STYLING_CONTRACT_VERSION,
  PORTAL_TEMPLATE_AUTHORING_CAPABILITY,
  isPortalTemplateCandidate,
  resolvePortalTemplateBlocks,
  type PortalExperiencePagePreviewInput,
  type PortalExperiencePagePutInput,
  type PortalExperiencePageSummary,
  type PortalExperiencePageView,
  type PortalPageKind,
  type PortalSettings,
  type PortalTemplateFinding,
} from "@v0y4ge/api-contracts";
import { formatICU, t as i18nT } from "../../../lib/i18n";
import { loadPublicEventsList, projectPublicEventToDesignerRow } from "../../../lib/events-public-data";
import { can } from "../../../shell/access";

/** The reserved slug of the event-listing slot. */
export const EVENT_LIST_SLOT = "events";

export { PORTAL_TEMPLATE_AUTHORING_CAPABILITY, PORTAL_STYLING_CONTRACT_VERSION };

/** A translated message with ICU parameters and a byte-exact English fallback. */
export function tf(key: string, fallback: string, params: Record<string, string | number>): string {
  return formatICU(i18nT(key, fallback), params);
}

/* ── Capabilities (the server enforces; these only decide what is offered) ── */

export interface TemplateCapabilities {
  /** Read pages, settings and preview published templates. */
  read: boolean;
  /** Choose the event-listing template (setPortalEventListTemplate). */
  chooseListTemplate: boolean;
  /** Change the tenant default detail template (settings PUT). */
  chooseDefaultDetailTemplate: boolean;
  /** Change one event's detail template (event PATCH). */
  chooseEventDetailTemplate: boolean;
  /** Author custom HTML (JRNI staff only). */
  author: boolean;
  /** Publish authored custom HTML (JRNI staff only). */
  publish: boolean;
}

export function templateCapabilities(): TemplateCapabilities {
  const staff = can(PORTAL_TEMPLATE_AUTHORING_CAPABILITY);
  return {
    read: can("journeys.read"),
    chooseListTemplate: can("journeys.write"),
    chooseDefaultDetailTemplate: can("journeys.write") && can("portal_settings.write"),
    chooseEventDetailTemplate: can("events.write"),
    author: staff && can("journeys.write"),
    publish: staff && can("journeys.publish"),
  };
}

/* ── Errors ──────────────────────────────────────────────────────────────── */

export interface TemplateError {
  status: number;
  code: string;
  message: string;
  /** Field-level findings (`path`, `message`, optional `line`). */
  fields: Array<{ path: string; message: string; line?: number }>;
}

/** Normalise anything thrown by the client into one shape the screens render. */
export function toTemplateError(err: unknown): TemplateError {
  const e = (err ?? {}) as { status?: unknown; code?: unknown; message?: unknown; fields?: unknown };
  const fields = Array.isArray(e.fields)
    ? (e.fields as Array<{ path?: unknown; message?: unknown; line?: unknown }>).map((f) => ({
        path: String(f.path ?? ""),
        message: String(f.message ?? ""),
        ...(typeof f.line === "number" ? { line: f.line } : {}),
      }))
    : [];
  return {
    status: typeof e.status === "number" ? e.status : 0,
    code: typeof e.code === "string" ? e.code : "UNKNOWN",
    message: typeof e.message === "string" && e.message ? e.message : i18nT("journeys.customTemplates.genericError", "Something went wrong."),
    fields,
  };
}

/** Field findings of an error, in the finding shape the editor shows. */
export function errorFindings(e: TemplateError): PortalTemplateFinding[] {
  return e.fields.map((f) => ({ code: e.code, path: f.path, message: f.message, ...(f.line ? { line: f.line } : {}) }));
}

/* ── Pages ───────────────────────────────────────────────────────────────── */

/** Every page of the tenant, followed across pages of the list. */
export async function listAllPages(): Promise<PortalExperiencePageSummary[]> {
  const out: PortalExperiencePageSummary[] = [];
  for (let page = 1; page <= 20; page++) {
    const res = await listPortalExperiencePages({ page, limit: 200 });
    out.push(...res.data);
    if (page >= res.pagination.total_pages) break;
  }
  return out;
}

/** Published custom-html pages of a kind, in title order. The listing slot itself is never a candidate. */
export function templateCandidates(pages: PortalExperiencePageSummary[], kind: PortalPageKind): PortalExperiencePageSummary[] {
  return pages
    .filter((p) => p.slug !== EVENT_LIST_SLOT && isPortalTemplateCandidate(p, kind))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/** Whether the server told us the live layout of each row (an older server does not). */
export function listCarriesLiveLayout(pages: PortalExperiencePageSummary[]): boolean {
  return pages.length === 0 || pages.some((p) => p.liveLayout !== undefined);
}

export function getPage(slug: string): Promise<PortalExperiencePageView> {
  return getPortalExperiencePage(slug).then((r) => r.data);
}

/** Slugs a NEW template may never take: the host's own addresses and the event-listing slot. */
export function reservedSlug(slug: string): boolean {
  return slug === EVENT_LIST_SLOT || (PORTAL_RESERVED_PAGE_SLUGS as readonly string[]).includes(slug);
}

/**
 * Whether a new template may be created at `slug`. Fails CLOSED: only a reserved-free
 * slug that no listed page carries AND whose own read answers 404 is free. Any other
 * answer (the page exists, or the check itself failed) is not free.
 */
export async function slugIsFree(slug: string): Promise<"free" | "taken" | "reserved"> {
  if (reservedSlug(slug)) return "reserved";
  const pages = await listAllPages();
  if (pages.some((p) => p.slug === slug)) return "taken";
  try {
    await getPage(slug);
    return "taken";
  } catch (err) {
    if (toTemplateError(err).status === 404) return "free";
    throw err;
  }
}

export function savePageDraft(slug: string, doc: PortalExperiencePagePutInput): Promise<PortalExperiencePageView> {
  return putPortalExperiencePage(slug, doc).then((r) => r.data);
}

export function publishPage(slug: string): Promise<{ page: PortalExperiencePageView; warnings: PortalTemplateFinding[] }> {
  return publishPortalExperiencePage(slug, {}).then((r) => ({ page: r.data, warnings: r.warnings ?? [] }));
}

export interface PreviewResult {
  /** The composed page, or null when the server composed nothing. */
  html: string | null;
  errors: PortalTemplateFinding[];
  warnings: PortalTemplateFinding[];
}

/** Server-composed preview. An empty document is "nothing composed", never a blank page. */
export function previewPage(slot: string, input: PortalExperiencePagePreviewInput): Promise<PreviewResult> {
  return previewPortalExperiencePage(slot, input).then((r) => ({
    html: r.data.html && r.data.html.trim() ? r.data.html : null,
    errors: r.data.errors,
    warnings: r.data.warnings,
  }));
}

/* ── Settings ────────────────────────────────────────────────────────────── */

export function readSettings(): Promise<PortalSettings> {
  return getPortalSettings().then((r) => r.data.settings);
}

/** Choose the event-listing template; `null` restores the standard page. Returns the settings read back. */
export async function applyListTemplate(templateSlug: string | null): Promise<PortalSettings> {
  await setPortalEventListTemplate({ templateSlug });
  return readSettings();
}

/**
 * Change the tenant default event-detail template. The settings PUT replaces the
 * document, and its contract requires `seo`, so the search setting is re-read
 * immediately before writing and sent back as it stands. `events.listTemplate` is
 * left out of the body on purpose, which keeps the stored value. Returns the
 * settings read back.
 */
export async function applyDefaultDetailTemplate(templateSlug: string | null): Promise<PortalSettings> {
  const current = await readSettings();
  await putPortalSettings({ seo: current.seo, events: { detailTemplate: templateSlug } });
  return readSettings();
}

/* ── The event catalogue for template choices ───────────────────────────── */

/** A real published event a template can be assigned to or previewed with. */
export interface TemplateEventOption {
  id: string;
  name: string;
  /** The event's public slug, which a detail preview renders. */
  slug: string;
  when?: string;
  /** The event's public path as the server declared it (`public_path`), when it declared one. */
  publicPath?: string;
}

/** Page size of the public events read (its contract caps `limit` at 100). */
const EVENT_CATALOG_PAGE = 100;
/** Pages followed at most (10,000 events). */
const EVENT_CATALOG_MAX_PAGES = 100;

/**
 * Every published event of the tenant, ended, sold-out and closed ones included, read
 * through the public events read and followed across its pages. Deliberately NOT the
 * Event List Page's own pool, which follows that page's display filters (for example
 * "Keep past events on the list"): choosing a template for an ended event must not
 * depend on how the standard list is set up. Ids and slugs are the server's; a row the
 * server sends without a slug cannot be previewed and is left out. If the server still
 * has more events once the safety bound is reached, the read fails as incomplete rather
 * than returning a partial list as if it were the whole catalogue.
 */
export async function loadTemplateEventCatalog(): Promise<TemplateEventOption[]> {
  const out: TemplateEventOption[] = [];
  const seen = new Set<string>();
  for (let page = 1; page <= EVENT_CATALOG_MAX_PAGES; page++) {
    const res = await loadPublicEventsList({ page, limit: EVENT_CATALOG_PAGE, upcoming: false, includeSoldOut: true, includeNonBookable: true });
    for (const row of res.data) {
      if (!row.slug || seen.has(row.id)) continue;
      seen.add(row.id);
      const projected = projectPublicEventToDesignerRow(row, "");
      out.push({ id: row.id, name: row.title, slug: row.slug, when: projected.when, ...(projected.publicPath ? { publicPath: projected.publicPath } : {}) });
    }
    if (!res.pagination.hasMore || page >= res.pagination.total_pages) return out;
  }
  throw Object.assign(
    new Error(tf("journeys.customTemplates.catalogIncomplete", "There are more than {count} published events, so not all of them could be listed here.", { count: EVENT_CATALOG_PAGE * EVENT_CATALOG_MAX_PAGES })),
    { code: "TEMPLATE_EVENT_CATALOG_INCOMPLETE" },
  );
}

/* ── One event ───────────────────────────────────────────────────────────── */

export interface EventTemplateState {
  id: string;
  title: string;
  /** The event's own detail template, or null when it inherits the default. */
  portalDetailTemplate: string | null;
}

export async function readEventTemplate(eventId: string): Promise<EventTemplateState> {
  const ev = await getStudioEvent(eventId, { baseUrl: "", surface: "events" });
  return { id: ev.id, title: ev.title, portalDetailTemplate: ev.portalDetailTemplate ?? null };
}

/** Set or clear one event's detail template. Only this event changes. Returns the event read back. */
export async function applyEventDetailTemplate(eventId: string, templateSlug: string | null): Promise<EventTemplateState> {
  await creation.updateEvent(eventId, { portalDetailTemplate: templateSlug });
  return readEventTemplate(eventId);
}

/* ── Authoring documents ─────────────────────────────────────────────────── */

/** The editable shape of a template document: one HTML source per locale and one CSS source. */
export interface TemplateDraft {
  slug: string;
  title: string;
  kind: PortalPageKind;
  locale: string;
  supportedLocales: string[];
  timeZone: string;
  html: Record<string, string>;
  css: string;
  /** Everything of the stored page this editor does not edit, carried back on save. Null: never saved. */
  base: PortalExperiencePageView | null;
}

/** The draft of a stored custom-html page. */
export function draftFromPage(page: PortalExperiencePageView): TemplateDraft {
  const html: Record<string, string> = {};
  for (const [tag, entry] of Object.entries(page.template?.locales ?? {})) html[tag] = entry.html;
  for (const tag of page.settings.supportedLocales) if (html[tag] === undefined) html[tag] = "";
  return {
    slug: page.slug,
    title: page.title,
    kind: page.settings.pageKind ?? "page",
    locale: page.settings.locale,
    supportedLocales: [...page.settings.supportedLocales],
    timeZone: page.settings.timeZone,
    html,
    css: page.template?.css ?? "",
    base: page,
  };
}

/** A new, unsaved template. */
export function blankDraft(slug: string, title: string, kind: PortalPageKind, locale: string, timeZone: string): TemplateDraft {
  return { slug, title, kind, locale, supportedLocales: [locale], timeZone, html: { [locale]: "" }, css: "", base: null };
}

/** The PUT body for a draft: the stored page with this editor's fields replaced. */
export function documentFromDraft(d: TemplateDraft): PortalExperiencePagePutInput {
  const base = d.base;
  const locales: Record<string, { html: string }> = {};
  for (const tag of d.supportedLocales) locales[tag] = { html: d.html[tag] ?? "" };
  const settings = {
    ...(base ? base.settings : { showHeader: true, showFooter: true }),
    navLabel: d.kind === "event-detail" ? null : (base ? base.settings.navLabel : null),
    locale: d.locale,
    supportedLocales: [...d.supportedLocales],
    timeZone: d.timeZone,
    layout: "custom-html" as const,
    pageKind: d.kind,
  };
  const template = {
    contractVersion: base?.template?.contractVersion ?? PORTAL_STYLING_CONTRACT_VERSION,
    locales,
    ...(d.css ? { css: d.css } : {}),
  };
  /* A block the template places with a typed component (<jrni-events id="os-events">) is the
     template's: the server rebuilds it from the component's attributes and refuses the save
     (template-blocks-conflict) when a sent copy differs, so any attribute change failed at Save
     draft (CC follow-up 2026-10-15). Those blocks are left out; blocks placed by <jrni-block ref>,
     or not placed at all, are sent as stored. The ids come from the contract's own resolver. */
  const stored = base ? base.blocks : [];
  const componentIds = new Set(resolvePortalTemplateBlocks({ template, blocks: [], mode: "save" }).derived.map((b) => b.id));
  return {
    title: d.title,
    header: base ? base.header : { eyebrow: null, title: null, subtitle: null },
    blocks: stored.filter((b) => !componentIds.has(b.id)),
    settings,
    template,
  } as PortalExperiencePagePutInput;
}

/** A stable fingerprint of what a save would send, to tell clean from dirty. */
export function draftFingerprint(d: TemplateDraft): string {
  return JSON.stringify({
    t: d.title, k: d.kind, l: d.locale, s: d.supportedLocales, z: d.timeZone,
    h: d.supportedLocales.map((tag) => d.html[tag] ?? ""), c: d.css,
  });
}

/** The findings (server or local) that point into one locale's HTML or into the CSS. */
export function findingsFor(findings: PortalTemplateFinding[], source: "html" | "css", locale: string): PortalTemplateFinding[] {
  const want = source === "css" ? "template.css" : `template.locales.${locale}.html`;
  return findings.filter((f) => f.path === want || f.path.startsWith(want + "."));
}
